import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import celebrationImg from '../assets/celebration.png';

const Screen6: React.FC = () => {
  const navigate = useNavigate();

  // SVG paths for checkmarks
  const checkPath = "M1.89048 4.53702L3.78092 6.42746L7.56178 2.64659";

  const CheckIcon = () => (
    <div className="bg-[#221a51] p-[2.593px] rounded-full">
      <svg className="w-[9px] h-[9px]" fill="none" viewBox="0 0 10 10">
        <path
          d={checkPath}
          stroke="white"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.3"
        />
      </svg>
    </div>
  );

  const handleSaveMore = () => {
    // Navigate back to start to restart the flow
    navigate('/');
  };

  return (
    <motion.div
      className="bg-[#f0effa] h-[100dvh] w-full relative overflow-y-auto overflow-x-hidden overscroll-contain"
      onClick={() => navigate('/')}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Congrats Title */}
      <motion.div
        className="absolute top-[64px] left-0 right-0 text-center"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring" }}
      >
        <motion.h1
          className="text-[45px] font-medium text-black font-['Bricolage_Grotesque',_sans-serif] leading-[45px]"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        >
          Congrats!
        </motion.h1>
      </motion.div>

      {/* First White Card - Benefits */}
      <motion.div
        className="absolute top-[160px] left-[48px] w-[294px] h-[270px] bg-white rounded-[30px] shadow-[6px_6px_0px_0px_#221a51]"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3, type: "spring" }}
        whileHover={{ y: -3, rotate: -0.4 }}
        whileTap={{ scale: 0.995 }}
      >
        {/* Small decorative bar */}
        <div className="absolute top-[44px] left-[55px] w-[60px] h-[6px] bg-[#9b86ea] opacity-20 rounded-full" />

        {/* Card Title */}
        <p className="absolute top-[12px] left-[42px] text-[15px] font-semibold text-black font-['Bricolage_Grotesque',_sans-serif] leading-[60px]">
          Congrats! You are on track to:
        </p>

        {/* Save $175 every month */}
        <motion.div
          className="absolute top-[84px] left-[30px] flex items-center gap-[15px]"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="bg-[#9b86ea] p-[10px] rounded-full shadow-[2.593px_2.593px_0px_0px_#221a51] border-[1.944px] border-[#221a51]">
            <CheckIcon />
          </div>
          <p className="text-[15px] font-medium text-black font-['Bricolage_Grotesque',_sans-serif]">
            Save $175 every month
          </p>
        </motion.div>

        {/* Simplify to 1 easy payment */}
        <motion.div
          className="absolute top-[147px] left-[30px] flex items-center gap-[15px]"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="bg-[#9b86ea] p-[10px] rounded-full shadow-[2.593px_2.593px_0px_0px_#221a51] border-[1.944px] border-[#221a51]">
            <CheckIcon />
          </div>
          <p className="text-[15px] font-medium text-black font-['Bricolage_Grotesque',_sans-serif]">
            Simplify to 1 easy payment
          </p>
        </motion.div>

        {/* Become debt-free faster */}
        <motion.div
          className="absolute top-[210px] left-[30px] flex items-center gap-[15px]"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="bg-[#9b86ea] p-[10px] rounded-full shadow-[2.593px_2.593px_0px_0px_#221a51] border-[1.944px] border-[#221a51]">
            <CheckIcon />
          </div>
          <p className="text-[15px] font-medium text-black font-['Bricolage_Grotesque',_sans-serif]">
            Become debt-free faster
          </p>
        </motion.div>
      </motion.div>

      {/* Second White Card - What Happens Next */}
      <motion.div
        className="absolute top-[500px] left-[48px] w-[294px] h-[166px] bg-white rounded-[30px] shadow-[6px_6px_0px_0px_#221a51]"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4, type: "spring" }}
        whileHover={{ y: -3, rotate: 0.4 }}
        whileTap={{ scale: 0.995 }}
      >
        <div className="absolute top-20px] left-[17px] p-[15px]">
          <p className="text-[15px] text-black font-['Bricolage_Grotesque',_sans-serif] leading-[25px]">
            <span className="font-semibold">What Happens Next:</span>
            <br />
            1. Lender reviews your application.
            <br />
            2. Receive a decision within 24 hours
            <br />
            3. Finalise first payment date
          </p>
        </div>
      </motion.div>

      {/* Celebration Image */}
      <motion.div
        className="absolute right-[-47px] top-[355px] w-[129px] h-[141px]"
        initial={{ rotate: -10, scale: 0 }}
        animate={{ rotate: 6.7, scale: 1, y: [0, -4, 0] }}
        transition={{
          delay: 0.8,
          type: "spring",
          stiffness: 200
        }}
      >
        <motion.img
          src={celebrationImg}
          alt="Celebration"
          className="w-full h-full object-contain"
          animate={{
            rotate: [6.7, 10, 6.7],
            scale: [1, 1.05, 1]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>

      {/* Save me more button */}
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          handleSaveMore();
        }}
        className="absolute top-[756px] left-[74px] w-[242px] bg-[#9b86ea] px-[12px] py-[12px] rounded-[50px] shadow-[6px_8px_0px_0px_#221a51] flex items-center justify-center hover:bg-[#8b76da] hover:shadow-[4px_6px_0px_0px_#221a51] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.9, type: "spring" }}
        whileTap={{ scale: 0.95 }}
      >
        <span className="text-white text-[20px] font-semibold font-['Bricolage_Grotesque',_sans-serif]">
          Save me more
        </span>
      </motion.button>

      {/* Confetti Animation */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-3 h-3 rounded-full"
          style={{
            backgroundColor: i % 2 === 0 ? '#9b86ea' : '#FFCF4D',
            left: `${Math.random() * 100}%`,
            top: '-20px'
          }}
          animate={{
            y: [0, window.innerHeight + 100],
            x: [(Math.random() - 0.5) * 100, (Math.random() - 0.5) * 200],
            rotate: [0, 360],
            opacity: [1, 0]
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            delay: 1 + i * 0.2,
            repeat: Infinity,
            repeatDelay: 2,
            ease: "easeIn"
          }}
        />
      ))}

      {/* Click to restart hint (subtle) */}
      {/* <motion.p
        className="absolute bottom-[20px] left-1/2 -translate-x-1/2 text-[12px] text-black/30 font-['Bricolage_Grotesque',_sans-serif]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        Tap anywhere to restart
      </motion.p> */}
    </motion.div>
  );
};

export default Screen6;
